import { useEffect, useState } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { Tooltip } from "../components/Tooltip";
import { useRules, useUpdateRule, type Rule, type RuleAction } from "../lib/rules";
import {
  RuleConflictNotice,
  RuleSummary,
} from "../components/RuleAction";
import { RULE_ACTION_OPTIONS, ruleConflictMessage } from "../lib/rule-action";
import {
  useAddDenylistEntry,
  useDenylist,
  useRemoveDenylistEntry,
} from "../lib/denylist";
import {
  useCreateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useWebhookEndpoints,
  useUpdateWebhookEndpoint,
} from "../lib/webhook-endpoints";
import { useInboundSources, useCapiDelivery, useBotFilter } from "../lib/project-tracking";
import { useApiKeys, useCreateApiKey, useRevokeApiKey, type ApiKey } from "../lib/api-keys";
import { apiUrl, ApiError } from "../lib/api";
import {
  CONSENT_REOPEN_SNIPPET,
  TrackingQualitySection,
} from "./project-tracking-tabs";
import { Trash2 } from "lucide-react";
import { Check, Copy, Eye, EyeOff, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Sparkline } from "../components/Sparkline";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { HqBadge } from "../components/HqBadge";
import { CustomHostnameManager } from "../components/CustomHostnameManager";
import { ProjectStatusChecklist } from "../components/ProjectStatusChecklist";
import { SettingRow, SettingsPanel } from "../components/SettingRow";
import { ProjectPausedWebhookBanner } from "../components/states";
import { Combobox } from "../components/Combobox";
import { showsPartnerOrigin } from "../lib/project-wizard";
import {
  COUNT_RULE_OPTIONS,
  DEFAULT_COUNT_INTERVAL_DAYS,
  shouldShowPurchaseHint,
  validateCountIntervalDaysInput,
  type CountRule,
} from "../lib/count-rule";
import {
  offersKundeLink,
  useCustomHostnameStatus,
  useDhCustomers,
} from "../lib/project-wizard";
import { AlertTriangle } from "lucide-react";
import {
  DISABLE_BQ_SYNC_CONFIRM_PHRASE,
  useProjectPipeline,
  useProjectCustomerLink,
  useRevealWebhookSecret,
  useRotateSecret,
  useRotateWebhookSecret,
  useSetProjectCustomerLink,
  useArchiveProject,
  useToggleActive,
  useToggleEngagementTracking,
  useToggleMergeReview,
  useToggleWebhookSigning,
  useUpdateEngine,
  useUpdateProject,
  type ProjectDetail,
  type ProjectPipelineRecentEvent,
} from "../lib/project-detail";
import { isFairUseHintDue } from "../lib/fair-use";
import { matchesRotateConfirmation } from "../lib/rotate-secret-confirm";

interface TabContext {
  project: ProjectDetail | undefined;
  isLoading: boolean;
  projectId: number;
}

function useTabContext(): TabContext {
  return useOutletContext<TabContext>();
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} kopiert`),
    () => toast.error("Kopieren fehlgeschlagen"),
  );
}

function LoadingShell() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-md border border-border-primary bg-bg-secondary"
        />
      ))}
    </div>
  );
}

// ---- Overview ----

/**
 * Top-of-Overview quality bar: is the CAPI delivering, and how much bot traffic
 * are we filtering? Cards stay hidden until there's data so a fresh project
 * isn't cluttered with zeros.
 */
function OverviewQualityBar({ projectId }: { projectId: number }) {
  const capi = useCapiDelivery(projectId);
  const bot = useBotFilter(projectId);

  const c = capi.data;
  const b = bot.data;
  const hasCapi = c && c.total_sent + c.total_failed > 0;
  const hasBot = b && b.total_seen > 0;
  if (!hasCapi && !hasBot) return null;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {hasCapi && (
        <div className="rounded-md border border-border-primary bg-bg-secondary p-4">
          <p className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
            CAPI-Auslieferung (30T)
          </p>
          <p className="mt-1 text-2xl font-semibold text-text-primary">
            {(c!.delivery_rate * 100).toFixed(1)}%
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">
            {c!.total_sent.toLocaleString("de-DE")} gesendet
            {c!.total_failed > 0 && (
              <span className="text-danger"> · {c!.total_failed.toLocaleString("de-DE")} fehlgeschlagen</span>
            )}
          </p>
        </div>
      )}
      {hasBot && (
        <div className="rounded-md border border-border-primary bg-bg-secondary p-4">
          <p className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
            Bot-Anteil (30T)
          </p>
          <p className="mt-1 text-2xl font-semibold text-warning">
            {(b!.block_rate * 100).toFixed(1)}%
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">
            {b!.total_seen.toLocaleString("de-DE")} geprüft · {b!.total_allowed.toLocaleString("de-DE")} Mensch · {b!.total_blocked.toLocaleString("de-DE")} geblockt
          </p>
        </div>
      )}
    </div>
  );
}

function FairUseNotice({ rejectionsToday }: { rejectionsToday: number }) {
  if (!isFairUseHintDue(rejectionsToday)) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-warning bg-warning-bg/40 px-3 py-2 text-xs text-text-primary">
      <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
      <div>
        <strong>Fair-Use heute {rejectionsToday}× angeschlagen.</strong> Die
        Kunden-Schnittstelle hat heute mehrfach abgelehnt (60 Abfragen/Minute,
        5.000/Tag). Sprich mit dem Kunden, bevor es zum Problem wird.
      </div>
    </div>
  );
}

export function ProjectOverviewTab() {
  const { project, isLoading, projectId } = useTabContext();
  const pipelineQuery = useProjectPipeline(projectId);
  if (isLoading || !project) return <LoadingShell />;

  const { stats } = project;
  const peak = stats.sparkline.length ? Math.max(...stats.sparkline) : 0;

  return (
    <div className="space-y-6">
      <ProjectStatusChecklist project={project} projectId={projectId} />

      <FairUseNotice rejectionsToday={project.fairUseRejectionsToday} />

      <PipelineCard pipeline={pipelineQuery.data} />

      <OverviewQualityBar projectId={projectId} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Events 24h" value={stats.last24h.toLocaleString("de-DE")} />
        <Stat
          label="Events 7 Tage"
          value={stats.sparkline
            .reduce((a, b) => a + b, 0)
            .toLocaleString("de-DE")}
        />
        <Stat
          label="Höhepunkt 7T"
          value={peak.toLocaleString("de-DE")}
        />
      </div>

      <div className="rounded-md border border-border-primary bg-bg-secondary p-5">
        <p className="mb-4 font-mono text-xs uppercase tracking-wider text-text-tertiary">
          Verlauf 7 Tage
        </p>
        {stats.available && stats.sparkline.length > 0 ? (
          <Sparkline
            data={stats.sparkline}
            width={640}
            height={120}
            ariaLabel={`7-Tage-Verlauf, Höhepunkt ${peak}`}
            className="w-full"
          />
        ) : (
          <p className="text-sm text-text-tertiary">
            Keine Statistik verfügbar.
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-md border border-border-primary">
        <div className="border-b border-border-primary bg-bg-secondary px-4 py-2.5">
          <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Event-Typ Verteilung (7T)
          </p>
        </div>
        {stats.eventTypeBreakdown.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-text-tertiary">
            Keine Events in den letzten 7 Tagen.
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {stats.eventTypeBreakdown.map((b) => (
                <tr
                  key={b.eventType}
                  className="border-b border-border-secondary last:border-0"
                >
                  <td className="px-4 py-2 font-medium text-text-primary">
                    {b.eventType}
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums text-text-primary">
                    {b.count.toLocaleString("de-DE")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border-primary bg-bg-secondary p-4">
      <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
        {label}
      </p>
      <p className="mt-2 font-mono text-2xl font-semibold tabular-nums text-text-primary">
        {value}
      </p>
    </div>
  );
}

function PipelineCard({
  pipeline,
}: {
  pipeline:
    | {
        queue: { waiting: number; active: number; delayed: number; failed: number };
        pendingApproval: number;
        processedToday: number;
        processed24h: number;
        recentEvents: ProjectPipelineRecentEvent[];
      }
    | undefined;
}) {
  return (
    <div className="rounded-md border border-border-primary bg-bg-secondary">
      <div className="flex items-center justify-between border-b border-border-primary px-5 py-3">
        <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
          Live Pipeline
        </p>
        <p className="text-xs text-text-tertiary">aktualisiert alle 5s</p>
      </div>

      <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-5">
        <PipelineStat
          label="In Queue"
          value={pipeline?.queue.waiting ?? 0}
          tone="neutral"
          hint="globally"
        />
        <PipelineStat
          label="Aktiv"
          value={pipeline?.queue.active ?? 0}
          tone="neutral"
          hint="globally"
        />
        <PipelineStat
          label="Pending Approval"
          value={pipeline?.pendingApproval ?? 0}
          tone={(pipeline?.pendingApproval ?? 0) > 0 ? "warn" : "neutral"}
        />
        <PipelineStat
          label="Heute verarbeitet"
          value={pipeline?.processedToday ?? 0}
          tone="success"
        />
        <PipelineStat
          label="Failed"
          value={pipeline?.queue.failed ?? 0}
          tone={(pipeline?.queue.failed ?? 0) > 0 ? "danger" : "neutral"}
          hint="globally"
        />
      </div>

      <div className="border-t border-border-primary">
        <div className="px-5 py-2.5">
          <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Letzte verarbeitete Events
          </p>
        </div>
        {!pipeline ? (
          <div className="px-5 py-6 text-center text-sm text-text-tertiary">
            Lade…
          </div>
        ) : pipeline.recentEvents.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-text-tertiary">
            Noch keine Events in der Recent-Liste (BullMQ behält die letzten ~500
            Jobs global). Sobald welche reinkommen, erscheinen sie hier.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border-secondary text-left">
              <tr>
                <th className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Typ
                </th>
                <th className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Vorname
                </th>
                <th className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Nachname
                </th>
                <th className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Email
                </th>
                <th className="px-4 py-2 text-right font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Wert
                </th>
                <th className="px-4 py-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Quelle
                </th>
                <th className="px-4 py-2 text-right font-mono text-xs uppercase tracking-wider text-text-tertiary">
                  Verarbeitet
                </th>
              </tr>
            </thead>
            <tbody>
              {pipeline.recentEvents.map((e) => (
                <tr
                  key={e.eventId}
                  className="border-b border-border-secondary last:border-0"
                >
                  <td className="px-4 py-2 font-medium text-text-primary">
                    {e.eventType}
                  </td>
                  <td className="px-4 py-2 text-text-secondary">
                    {e.firstName ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-text-secondary">
                    {e.lastName ?? "—"}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-text-secondary">
                    {e.email ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums text-text-primary">
                    {e.dealValue != null
                      ? `${e.dealValue.toLocaleString("de-DE")} ${e.dealCurrency ?? ""}`.trim()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-text-secondary">
                    {e.source ?? "—"}
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-text-tertiary">
                    {formatRelativeTime(e.finishedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PipelineStat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: number;
  tone: "neutral" | "success" | "warn" | "danger";
  hint?: string;
}) {
  const valueClass = {
    neutral: "text-text-primary",
    success: "text-success",
    warn: "text-warning",
    danger: "text-danger",
  }[tone];
  return (
    <div className="rounded-md border border-border-secondary bg-bg-primary p-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
        {label}
        {hint ? (
          <span className="ml-1 normal-case tracking-normal text-text-tertiary">
            ({hint})
          </span>
        ) : null}
      </p>
      <p className={`mt-1.5 font-mono text-xl font-semibold tabular-nums ${valueClass}`}>
        {value.toLocaleString("de-DE")}
      </p>
    </div>
  );
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `vor ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  return `vor ${d} T.`;
}

// ---- Tracking-Skript (vorher "Domains") ----

export function ProjectTrackingTab() {
  const { project, projectId, isLoading } = useTabContext();

  // Prefer the customer's custom hostname if registered. Show a warning when
  // it's pending so the user understands the snippet won't work until active.
  const chStatus = useCustomHostnameStatus(
    project?.customHostname ? projectId : null,
    false,
  );

  if (isLoading || !project) return <LoadingShell />;

  const customHostnameActive =
    chStatus.data?.cfStatus === "active" &&
    chStatus.data?.sslStatus === "active";

  const trackerSnippet = project.trackerSnippet;
  const iframeTrackerSnippet = project.iframeTrackerSnippet;
  const customHostnamePending =
    Boolean(project.customHostname) && !customHostnameActive;

  return (
    <SettingsPanel>
      <SettingRow
        title="Tracking-Subdomain"
        description="Eine fertige Web-Adresse für dein Tracking. Funktioniert sofort, ohne dass beim Kunden etwas eingerichtet werden muss."
      >
        {project.subdomain ? (
          <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
            <span className="font-mono text-sm text-text-primary">
              {project.subdomain}.dhc-track.io
            </span>
            <button
              type="button"
              onClick={() =>
                copyToClipboard(
                  `${project.subdomain}.dhc-track.io`,
                  "Subdomain",
                )
              }
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
            >
              <Copy size={12} /> Kopieren
            </button>
          </div>
        ) : (
          <p className="text-sm text-text-tertiary">
            Keine Subdomain registriert.
          </p>
        )}
      </SettingRow>

      <SettingRow
        title="Custom Hostname"
        description="Optional: Tracking läuft über die eigene Adresse des Kunden (z. B. t.kunde.de) statt über unsere. Das wirkt vertrauenswürdiger und die Messung hält länger."
      >
        <CustomHostnameManager
          projectId={projectId}
          hasRegistered={Boolean(project.customHostname)}
        />
      </SettingRow>

      <SettingRow
        title="Erlaubte Domains"
        description="Auf welchen Webseiten dein Tracking laufen darf. Trag die Adressen deiner Landingpages/Funnel ein — woanders wird nichts erfasst."
      >
        <AllowedDomainsSection project={project} projectId={projectId} />
      </SettingRow>

      {trackerSnippet && (
        <SettingRow
          title="Script-Tag"
          description="Diese zwei Zeilen baust du (oder dein Webentwickler) oben auf jeder Funnel-Seite ein — damit beginnt die Messung."
        >
          {customHostnamePending && (
            <div className="mb-2 flex items-start gap-2 rounded-md border border-warning bg-warning-bg/40 px-3 py-2 text-xs text-text-primary">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
              <div>
                <strong>Eigene Adresse noch nicht aktiv.</strong> Der Code nutzt
                schon <code>{project.customHostname}</code> — er funktioniert
                aber erst, wenn die technische Einrichtung beim Kunden fertig ist
                (DNS-Eintrag und SSL-Zertifikat). Bis dahin wird über diese
                Adresse nichts gemessen.
              </div>
            </div>
          )}
          <pre className="overflow-auto rounded-md border border-border-primary bg-bg-tertiary p-3 font-mono text-xs leading-relaxed text-text-primary">
            {trackerSnippet}
          </pre>
          <button
            type="button"
            onClick={() => copyToClipboard(trackerSnippet, "Script-Tag")}
            className="mt-2 inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
          >
            <Copy size={12} /> Snippet kopieren
          </button>
        </SettingRow>
      )}

      {iframeTrackerSnippet && (
        <SettingRow
          title="iframe-Bridge"
          description="Für Formulare, die in einem fremden Tool eingebettet sind (z. B. EverWebinar, Calendly). Dieser Code wird in dem Tool eingebaut und gibt eingegebene E-Mail/Telefon an deine Seite weiter, damit der Lead richtig zugeordnet wird. Das normale Tracking-Skript muss dort ebenfalls laufen."
        >
          <pre className="overflow-auto rounded-md border border-border-primary bg-bg-tertiary p-3 font-mono text-xs leading-relaxed text-text-primary">
            {iframeTrackerSnippet}
          </pre>
          <button
            type="button"
            onClick={() =>
              copyToClipboard(iframeTrackerSnippet, "iframe-Snippet")
            }
            className="mt-2 inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
          >
            <Copy size={12} /> Snippet kopieren
          </button>
        </SettingRow>
      )}

      <SettingRow
        title="Link für die Datenschutzerklärung"
        description="Diesen Link in die Datenschutzerklärung einbauen. Ein Klick öffnet jederzeit die Cookie-Einstellungen — zusätzlich zum Schloss-Symbol unten rechts auf der Seite."
      >
        <div className="flex items-start gap-2">
          <code className="flex-1 overflow-x-auto whitespace-pre rounded-md border border-border-primary bg-bg-tertiary px-3 py-2 font-mono text-xs text-text-primary">
            {CONSENT_REOPEN_SNIPPET}
          </code>
          <button
            type="button"
            onClick={() => copyToClipboard(CONSENT_REOPEN_SNIPPET, "Snippet")}
            className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
          >
            <Copy size={12} /> Kopieren
          </button>
        </div>
      </SettingRow>
    </SettingsPanel>
  );
}

// ---- Erlaubte Domains (Settings) ----

function AllowedDomainsSection({
  project,
  projectId,
}: {
  project: ProjectDetail;
  projectId: number;
}) {
  const update = useUpdateProject(projectId);
  const [draft, setDraft] = useState<string[]>(project.allowedDomains);
  const [input, setInput] = useState("");

  useEffect(() => {
    setDraft(project.allowedDomains);
  }, [project.allowedDomains]);

  const addDomain = () => {
    const d = input
      .trim()
      .toLowerCase()
      .replace(/^[a-z]+:\/\//, "")
      .split("/")[0]!
      .split("?")[0]!
      .split(":")[0]!
      .replace(/^\*\./, "");
    if (!d) return;
    if (draft.includes(d)) {
      toast.error("Domain bereits vorhanden");
      return;
    }
    setDraft((s) => [...s, d]);
    setInput("");
  };

  const removeDomain = (d: string) => {
    setDraft((s) => s.filter((x) => x !== d));
  };

  const dirty =
    JSON.stringify([...draft].sort()) !==
    JSON.stringify([...project.allowedDomains].sort());

  const save = () => {
    update.mutate(
      { allowedDomains: draft },
      {
        onSuccess: () => toast.success("Domains gespeichert"),
        onError: (err) =>
          toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  return (
    <>
      {project.allowedDomains.length === 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-warning bg-warning-bg px-3 py-2 text-xs text-text-secondary">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
          <span>
            <strong className="text-text-primary">Noch keine Webseiten eingetragen.</strong>{" "}
            Solange hier nichts steht, läuft das Tracking überall — auch wenn jemand deinen Code auf
            einer fremden Seite einbaut. Trag die Adressen deiner eigenen Seiten ein, damit nur dort
            gemessen wird.
          </span>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {draft.map((d) => (
          <span
            key={d}
            className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 font-mono text-xs text-text-primary"
          >
            {d}
            <button
              type="button"
              onClick={() => removeDomain(d)}
              aria-label={`${d} entfernen`}
              className="text-text-tertiary hover:text-danger"
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addDomain();
            }
          }}
          placeholder="z.B. landingpage.de"
          className="h-9 flex-1 rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
        />
        <button
          type="button"
          onClick={addDomain}
          className="rounded-md border border-border-primary bg-bg-secondary px-3 text-sm font-medium text-text-primary hover:bg-bg-hover"
        >
          Hinzufügen
        </button>
      </div>
      {dirty && (
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setDraft(project.allowedDomains)}
            className="text-sm text-text-secondary hover:text-text-primary"
          >
            Zurücksetzen
          </button>
          <button
            type="button"
            onClick={save}
            disabled={update.isPending}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-60"
          >
            Speichern
          </button>
        </div>
      )}
    </>
  );
}

// ---- Integrations ----

export function ProjectIntegrationsTab() {
  const { project, projectId, isLoading } = useTabContext();

  if (isLoading || !project) return <LoadingShell />;

  return (
    <SettingsPanel>
      {!project.active && (
        <div className="mb-4">
          <ProjectPausedWebhookBanner />
        </div>
      )}
      <SettingRow
        title="Webhook-Endpoints (KI-Auto-Integrator)"
        description="Lege für jede Ereignis-Art (z. B. „Lead“) eine eigene Eingangs-Adresse an. Dein Tool schickt einmal ein Beispiel, und die KI ordnet die Felder automatisch zu — du brauchst dafür kein Make/Zapier mehr."
        wide
      >
        <WebhookEndpointsSection
          projectId={projectId}
          signingDefault={project.webhookSigningRequired}
          showOrigin={showsPartnerOrigin(project.workspace)}
        />
      </SettingRow>

      <SettingRow
        title="Direkt angebundene Quellen (automatisch erkannt)"
        description="Jedes Tool, das von außen Ereignisse an DHC Track schickt (z. B. über Make/Zapier), taucht hier automatisch auf — mit erkanntem Tool, wie oft es sendet und den zuletzt gesendeten Daten."
        wide
      >
        <InboundSourcesSection projectId={projectId} />
      </SettingRow>

      <SettingRow
        title="Webhook-Signing"
        description="Lässt nur Daten durch, die einen geheimen Schlüssel mitschicken — so kann niemand Fremdes gefälschte Ereignisse einschleusen. Ist es aktiv, werden Sendungen ohne gültigen Schlüssel abgewiesen."
        wide
      >
        <WebhookSigningSection project={project} />
      </SettingRow>

      <SettingRow
        title="Schnittstellen-Zugänge"
        description="Zugänge für die lesende Kunden-Schnittstelle (z. B. Make, Zapier, eine Agentur). Jeder Zugang hat ein eigenes Geheimnis und kann einzeln entzogen werden. Das ist der Zugang für Kunden — nicht das Projekt-Geheimnis aus den Einstellungen (das darf nie an Kunden weitergegeben werden)."
        wide
      >
        <ApiKeysSection projectId={projectId} />
      </SettingRow>
    </SettingsPanel>
  );
}

/**
 * S2 — customer-API access keys. Follows the same one-time-reveal pattern
 * as the project API secret in ProjectSettingsTab (copy, then hide for
 * good — the plaintext is never fetchable again after creation).
 */
function ApiKeysSection({ projectId }: { projectId: number }) {
  const { data, isLoading } = useApiKeys(projectId);
  const create = useCreateApiKey(projectId);
  const revoke = useRevokeApiKey(projectId);
  const [name, setName] = useState("");
  const [revealed, setRevealed] = useState<{ name: string; secret: string } | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKey | null>(null);

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      { name: trimmed },
      {
        onSuccess: (res) => {
          setRevealed({ name: res.name, secret: res.secret });
          setName("");
        },
        onError: (err) => {
          // G3/TASK-124 C6 — prefer the server's German message (e.g.
          // "project_inactive") over the generic "400 Bad Request".
          const message =
            err instanceof ApiError
              ? ((err.body as { message?: string })?.message ?? err.message)
              : (err as Error).message;
          toast.error(`Anlegen fehlgeschlagen: ${message}`);
        },
      },
    );
  };

  const handleRevoke = () => {
    if (!confirmRevoke) return;
    revoke.mutate(confirmRevoke.id, {
      onSuccess: () => toast.success("Zugang entzogen"),
      onError: (err) =>
        toast.error(`Entziehen fehlgeschlagen: ${(err as Error).message}`),
    });
    setConfirmRevoke(null);
  };

  const items = data?.items ?? [];

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name, z. B. Make oder Zapier"
          className="h-9 flex-1 rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={!name.trim() || create.isPending}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-60"
        >
          Zugang anlegen
        </button>
      </div>

      {revealed && (
        <div className="rounded-md border border-border-primary bg-bg-secondary p-3 text-xs">
          <p className="mb-1 text-text-secondary">
            Geheimnis für „{revealed.name}“ — jetzt kopieren, danach nicht mehr
            im Klartext sichtbar:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-md border border-border-primary bg-bg-tertiary px-3 py-1.5 font-mono text-text-primary">
              {revealed.secret}
            </code>
            <button
              type="button"
              onClick={() => copyToClipboard(revealed.secret, "Geheimnis")}
              className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-text-secondary hover:bg-bg-hover"
            >
              <Copy size={12} /> Kopieren
            </button>
            <button
              type="button"
              onClick={() => setRevealed(null)}
              className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-text-secondary hover:bg-bg-hover"
            >
              <EyeOff size={12} /> Verbergen
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <LoadingShell />
      ) : items.length === 0 ? (
        <p className="text-xs text-text-tertiary">Noch keine Zugänge angelegt.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((k) => (
            <li
              key={k.id}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs"
            >
              <span className="font-medium text-text-primary">{k.name}</span>
              <code className="rounded bg-bg-tertiary px-1.5 py-0.5 font-mono text-text-secondary">
                dhk_{k.prefix}…
              </code>
              <span className="text-text-tertiary">
                angelegt {new Date(k.createdAt).toLocaleDateString("de-DE")}
              </span>
              <span className="text-text-tertiary">
                zuletzt genutzt{" "}
                {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString("de-DE") : "nie"}
              </span>
              <span
                className={
                  k.status === "active"
                    ? "rounded bg-success-bg px-1.5 py-0.5 text-success"
                    : "rounded bg-bg-tertiary px-1.5 py-0.5 text-text-tertiary"
                }
              >
                {k.status === "active" ? "aktiv" : "entzogen"}
              </span>
              {k.status === "active" && (
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(k)}
                  className="ml-auto rounded border border-danger px-2 py-0.5 text-danger hover:bg-danger-bg"
                >
                  Entziehen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-text-tertiary">
        Anleitung für die Ansprechperson des Kunden:{" "}
        <a
          href={apiUrl("/v1/docs")}
          target="_blank"
          rel="noreferrer"
          className="text-accent underline"
        >
          {apiUrl("/v1/docs")}
        </a>
      </p>

      <ConfirmDialog
        open={confirmRevoke !== null}
        onOpenChange={(open) => !open && setConfirmRevoke(null)}
        title="Zugang entziehen?"
        variant="danger"
        confirmLabel="Entziehen"
        description={
          <>
            Der Zugang „{confirmRevoke?.name}“ funktioniert danach nicht mehr.
            Das kann nicht rückgängig gemacht werden.
          </>
        }
        onConfirm={handleRevoke}
      />
    </div>
  );
}

const TRANSPORT_LABEL: Record<string, string> = {
  webhook: "Direkt-Webhook (/webhook)",
  webhook_slug: "Benannter Endpoint",
  browser: "Browser (t.js)",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} h`;
  return `vor ${Math.floor(h / 24)} d`;
}

/**
 * Passively-discovered inbound sources: anything POSTing directly to /webhook
 * (funnel_mark-keyed) without a named endpoint shows up here, so the operator
 * sees what's sending data, from where (detected platform / User-Agent), how
 * often, and can inspect the last payloads.
 */
function InboundSourcesSection({ projectId }: { projectId: number }) {
  const { data, isLoading } = useInboundSources(projectId);
  const [open, setOpen] = useState<number | null>(null);

  if (isLoading) return <LoadingShell />;
  const sources = data?.sources ?? [];
  if (sources.length === 0) {
    return (
      <p className="text-xs text-text-tertiary">
        Noch keine angebundenen Tools erkannt. Sobald von außen (z. B. über Make/Zapier)
        Ereignisse an dieses Projekt geschickt werden, erscheinen sie hier automatisch.
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {sources.map((s) => (
        <li key={s.id} className="rounded-md border border-border-primary bg-bg-secondary text-xs">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2">
            {s.detected_platform && (
              <span className="rounded bg-accent-subtle px-1.5 py-0.5 font-semibold uppercase tracking-wider text-accent">
                {s.detected_platform}
              </span>
            )}
            <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-text-secondary">
              {TRANSPORT_LABEL[s.transport] ?? s.transport}
            </span>
            <span className="font-mono text-text-primary">{s.event_type ?? "—"}</span>
            <span className="text-text-tertiary">{s.event_count}× · {timeAgo(s.last_seen)}</span>
            <button
              type="button"
              onClick={() => setOpen(open === s.id ? null : s.id)}
              className="ml-auto rounded border border-border-primary px-2 py-0.5 text-text-secondary hover:bg-bg-hover"
            >
              {open === s.id ? "Payloads verbergen" : "Letzte Payloads"}
            </button>
          </div>
          {s.client_ua && (
            <div className="border-t border-border-primary px-3 py-1 font-mono text-[10px] text-text-tertiary">
              UA: {s.client_ua.slice(0, 120)}
            </div>
          )}
          {open === s.id && (
            <div className="space-y-2 border-t border-border-primary px-3 py-2">
              {s.sample_payloads.length === 0 ? (
                <p className="text-text-tertiary">Keine Payloads gespeichert.</p>
              ) : (
                s.sample_payloads.map((p, i) => (
                  <div key={i}>
                    <div className="text-[10px] text-text-tertiary">{new Date(p.at).toLocaleString()}</div>
                    <pre className="overflow-x-auto rounded bg-bg-primary p-2 font-mono text-[10px] text-text-secondary">
                      {JSON.stringify(p.payload, null, 2).slice(0, 2000)}
                    </pre>
                  </div>
                ))
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---- Rules ----
//
// Shows two groups for this project:
//   1. Globale Rules (read-only here — bearbeiten erfolgt im /rules-Bereich
//      damit aus Versehen niemand eine globale Rule projekt-spezifisch
//      kaputtmacht; der Link führt direkt dorthin).
//   2. Projekt-spezifische Rules (editierbar + löschbar, "+ Neue Rule"-Button
//      öffnet die volle Rule-Page mit voreingestelltem Scope).

/** A project-only rule: summary, the kind switchable in place, link to the full editor. */
function ProjectRuleRow({ rule: r }: { rule: Rule }) {
  const navigate = useNavigate();
  const update = useUpdateRule(r.id);
  const conflict = ruleConflictMessage(update.error);
  const setAction = (action: RuleAction) =>
    update.mutate(
      {
        isGlobal: r.is_global,
        projectIds: r.project_ids,
        eventType: r.event_type,
        condition: r.condition,
        action,
      },
      {
        onSuccess: () => toast.success("Art der Regel geändert"),
        onError: (err) => {
          if (ruleConflictMessage(err) === null) {
            toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
          }
        },
      },
    );
  return (
    <li className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
      <div className="flex items-center gap-3">
        <RuleSummary rule={r} />
        <select
          value={r.action}
          disabled={update.isPending}
          onChange={(e) => setAction(e.target.value as RuleAction)}
          aria-label="Art der Regel"
          className="h-7 rounded-md border border-border-primary bg-bg-primary px-2 text-[11px] text-text-primary focus:border-border-focus focus:outline-none"
        >
          {RULE_ACTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => navigate(`/rules/${r.id}`)}
          className="rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-[11px] text-text-secondary hover:bg-bg-hover"
        >
          Bearbeiten
        </button>
      </div>
      {conflict && (
        <div className="mt-2">
          <RuleConflictNotice message={conflict} />
        </div>
      )}
    </li>
  );
}

export function ProjectRulesTab() {
  const { project, projectId } = useTabContext();
  const rulesQuery = useRules({ project: projectId });
  const navigate = useNavigate();

  if (!project) return <LoadingShell />;

  const items = rulesQuery.data?.items ?? [];
  const globalRules = items.filter((r) => r.is_global);
  const projectRules = items.filter((r) => !r.is_global);

  return (
    <SettingsPanel>
      <SettingRow
        title="Globale Approval-Rules"
        description="Regeln, die für alle Projekte gleichzeitig gelten, auch die mitgelieferten (zum Beispiel Käufe mit Wert 0 ablehnen). Hier kannst du sie nur ansehen; geändert oder ausgeschaltet werden sie im Bereich „Rules“."
        wide
      >
        {!rulesQuery.isLoading && (
          <p className="mb-2 text-xs text-text-tertiary">
            {globalRules.length} aktiv
          </p>
        )}
        {rulesQuery.isLoading ? (
          <LoadingShell />
        ) : globalRules.length === 0 ? (
          <p className="text-xs text-text-tertiary">
            Keine globalen Rules aktiv.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {globalRules.map((r) => (
              <li
                key={r.id}
                className="flex items-center gap-3 rounded-md border border-border-primary bg-bg-secondary px-3 py-2"
              >
                <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                  global
                </span>
                <RuleSummary rule={r} />
                <button
                  type="button"
                  onClick={() => navigate(`/rules/${r.id}`)}
                  className="text-[11px] text-text-link hover:text-text-link-hover"
                >
                  Im Rules-Bereich öffnen →
                </button>
              </li>
            ))}
          </ul>
        )}
      </SettingRow>

      <SettingRow
        title="Nur für dieses Projekt"
        description="Zusätzliche Regeln, die nur für dieses eine Projekt gelten. Die Art (prüfen, immer zulassen, immer ablehnen) stellst du direkt hier ein."
        wide
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-text-tertiary">
            {projectRules.length} aktiv
          </p>
          <button
            type="button"
            onClick={() =>
              navigate(`/rules/new?projectId=${projectId}`)
            }
            className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-text-on-accent hover:bg-accent-hover"
          >
            + Neue Approval-Rule
          </button>
        </div>
        {rulesQuery.isLoading ? (
          <LoadingShell />
        ) : projectRules.length === 0 ? (
          <p className="mt-3 text-xs text-text-tertiary">
            Keine eigenen Prüf-Regeln für dieses Projekt. Die allgemeinen Regeln von oben gelten trotzdem.
          </p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {projectRules.map((r) => (
              <ProjectRuleRow key={r.id} rule={r} />
            ))}
          </ul>
        )}
      </SettingRow>
    </SettingsPanel>
  );
}

// ---------------------------------------------------------------------------
// Advanced Analytics — engagement tracking (PageDuration + ScrollDepth).
// Default off per project; toggle controls a flag served via /t.js so the
// tracker only emits these events when the operator turns them on.
// ---------------------------------------------------------------------------

function EngagementTrackingSection({
  project,
}: {
  project: ProjectDetail;
}) {
  const toggle = useToggleEngagementTracking(project.id);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() =>
            toggle.mutate(!project.engagementTrackingEnabled, {
              onSuccess: (res) =>
                toast.success(
                  res.engagementTrackingEnabled
                    ? "Advanced Analytics aktiviert — t.js Cache TTL 5 min, Wirkung nach max ~5 min"
                    : "Advanced Analytics deaktiviert",
                ),
              onError: (err) =>
                toast.error(`Schalten fehlgeschlagen: ${(err as Error).message}`),
            })
          }
          disabled={toggle.isPending}
          className={`rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-60 ${
            project.engagementTrackingEnabled
              ? "border border-border-primary bg-bg-secondary text-text-secondary hover:bg-bg-hover"
              : "bg-accent text-text-on-accent hover:bg-accent-hover"
          }`}
        >
          {project.engagementTrackingEnabled
            ? "Advanced Analytics ausschalten"
            : "Advanced Analytics aktivieren"}
        </button>
        <span
          className={`text-xs font-medium ${
            project.engagementTrackingEnabled ? "text-success" : "text-text-tertiary"
          }`}
        >
          {project.engagementTrackingEnabled ? "Aktiv" : "Aus"}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Denylist section — list + add + remove. The "ban" button lives in the
// merge-review drawer (it's the natural moment when a reviewer sees a
// recycled value), so this section is mostly a read+delete view.
// ---------------------------------------------------------------------------

function DenylistSection({ projectId }: { projectId: number }) {
  const query = useDenylist(projectId);
  const add = useAddDenylistEntry(projectId);
  const remove = useRemoveDenylistEntry(projectId);
  const [draftType, setDraftType] = useState("");
  const [draftValue, setDraftValue] = useState("");
  const [draftReason, setDraftReason] = useState("");

  const items = query.data?.items ?? [];

  const submit = () => {
    if (!draftType.trim() || !draftValue.trim()) return;
    add.mutate(
      {
        keyType: draftType.trim(),
        value: draftValue.trim(),
        reason: draftReason.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Auf Denylist gesetzt");
          setDraftType("");
          setDraftValue("");
          setDraftReason("");
        },
        onError: (err) =>
          toast.error(`Hinzufügen fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border-primary bg-bg-secondary p-3">
        <div className="min-w-[120px] flex-1">
          <label className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
            key_type
          </label>
          <input
            type="text"
            value={draftType}
            onChange={(e) => setDraftType(e.target.value)}
            placeholder="email / phone / fbclid …"
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
          />
        </div>
        <div className="min-w-[200px] flex-[2]">
          <label className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
            value (normalisiert)
          </label>
          <input
            type="text"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            placeholder="z.B. test@test.com"
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
          />
        </div>
        <div className="min-w-[160px] flex-[2]">
          <label className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
            Grund (optional)
          </label>
          <input
            type="text"
            value={draftReason}
            onChange={(e) => setDraftReason(e.target.value)}
            placeholder="Recycle / Fake / …"
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!draftType.trim() || !draftValue.trim() || add.isPending}
          className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
        >
          + Hinzufügen
        </button>
      </div>

      {query.isLoading ? (
        <LoadingShell />
      ) : items.length === 0 ? (
        <p className="text-xs text-text-tertiary">
          Noch keine Einträge. Über den 🚫-Button im Merge-Review-Drawer landen
          Werte hier.
        </p>
      ) : (
        <ul className="space-y-1">
          {items.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center gap-3 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs"
            >
              <span className="w-20 flex-shrink-0 font-mono text-text-tertiary">
                {entry.key_type}
              </span>
              <span className="flex-1 truncate font-mono text-text-primary">
                {entry.value}
              </span>
              {entry.reason && (
                <span className="hidden truncate text-text-tertiary md:inline-block md:max-w-[200px]">
                  {entry.reason}
                </span>
              )}
              <span className="hidden flex-shrink-0 text-[10px] text-text-tertiary md:inline">
                {new Date(entry.created_at).toLocaleDateString("de-DE")}
              </span>
              <button
                type="button"
                onClick={() =>
                  remove.mutate(entry.id, {
                    onSuccess: () => toast.success("Entfernt"),
                  })
                }
                className="text-text-tertiary hover:text-danger"
                title="Entfernen"
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Webhook-Endpoints (KI-Auto-Integrator) section — list + create form.
// Detail/Wizard view lives in WebhookEndpointDetailPage.
// ---------------------------------------------------------------------------

const EVENT_TYPE_PRESETS = [
  "Lead",
  "HQLead",
  "WebinarAttendance",
  "Contact",
  "Purchase",
] as const;
const CUSTOM_EVENT_TYPE = "__custom__";

function EndpointSigningToggle({
  projectId,
  endpointId,
  value,
  signingDefault,
}: {
  projectId: number;
  endpointId: number;
  value: boolean | null;
  signingDefault: boolean;
}) {
  const update = useUpdateWebhookEndpoint(projectId, endpointId);
  // Effective requirement: explicit override wins, else the project default.
  const effective = value ?? signingDefault;
  return (
    <label
      className="flex items-center gap-1 text-[10px] text-text-tertiary"
      title="Webhook-Signing für diese Integration erforderlich (erbt sonst die Projekt-Einstellung)"
    >
      <input
        type="checkbox"
        checked={effective}
        disabled={update.isPending}
        onChange={(ev) => update.mutate({ signingRequired: ev.target.checked })}
      />
      Signing
    </label>
  );
}

function WebhookEndpointsSection({
  projectId,
  signingDefault,
  showOrigin,
}: {
  projectId: number;
  signingDefault: boolean;
  showOrigin: boolean;
}) {
  const navigate = useNavigate();
  const query = useWebhookEndpoints(projectId);
  const create = useCreateWebhookEndpoint(projectId);
  const remove = useDeleteWebhookEndpoint(projectId);
  const [draftName, setDraftName] = useState("");
  const [draftEventType, setDraftEventType] = useState("Lead");
  const [draftCustomEventType, setDraftCustomEventType] = useState("");
  const [draftCountRule, setDraftCountRule] = useState<CountRule>("every");
  const [draftCountIntervalDays, setDraftCountIntervalDays] = useState(
    String(DEFAULT_COUNT_INTERVAL_DAYS),
  );

  const items = query.data?.items ?? [];

  const eventType =
    draftEventType === CUSTOM_EVENT_TYPE
      ? draftCustomEventType.trim()
      : draftEventType;
  const daysValidation = validateCountIntervalDaysInput(draftCountIntervalDays, draftCountRule);

  const submit = () => {
    if (!draftName.trim() || !eventType || !daysValidation.ok) return;
    create.mutate(
      {
        name: draftName.trim(),
        eventType,
        countRule: draftCountRule,
        countIntervalDays: daysValidation.value,
      },
      {
        onSuccess: (res) => {
          toast.success("Endpoint angelegt");
          setDraftName("");
          navigate(`/projects/${projectId}/webhook-endpoints/${res.id}`);
        },
        onError: (err) =>
          toast.error(`Anlegen fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border-primary bg-bg-secondary p-3">
        <div className="min-w-[200px] flex-[2]">
          <label className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
            Name (für dich)
          </label>
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="z.B. Lead aus Webinar A"
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
          />
        </div>
        <div className="min-w-[140px] flex-1">
          <label className="mb-1 flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
            Event-Typ
            <Tooltip
              content={
                "Empfohlene Meta-Standard-Events (sparsam senden — nur was zählt):\n" +
                "• CompleteRegistration — Freebie-/Webinar-Leads (Eintragungen)\n" +
                "• Schedule — Terminbuchung / Erstgespräch (z.B. Calendly)\n" +
                "• Purchase — Käufe (mit value + currency)\n" +
                "• Lead — allgemeine Lead-Conversion\n" +
                "• Contact — Kontaktformular\n" +
                "Nicht jeden Mini-Schritt (Submit/ViewContent/Start) an Meta — das verwässert den Algorithmus."
              }
            >
              <span className="cursor-help rounded-full border border-border-primary px-1 text-[9px] not-italic text-text-tertiary">
                ?
              </span>
            </Tooltip>
          </label>
          <select
            value={draftEventType}
            onChange={(e) => setDraftEventType(e.target.value)}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none"
          >
            {EVENT_TYPE_PRESETS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
            <option value={CUSTOM_EVENT_TYPE}>Anderes Conversion Event …</option>
          </select>
        </div>
        {draftEventType === CUSTOM_EVENT_TYPE && (
          <div className="min-w-[140px] flex-1">
            <label className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
              Eigener Event-Name
            </label>
            <input
              type="text"
              value={draftCustomEventType}
              onChange={(e) => setDraftCustomEventType(e.target.value)}
              placeholder="z.B. CompleteRegistration"
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
            />
          </div>
        )}
        <div className="min-w-[180px] flex-1">
          <label className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
            Zählregel
          </label>
          <select
            value={draftCountRule}
            onChange={(e) => setDraftCountRule(e.target.value as CountRule)}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none"
          >
            {COUNT_RULE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {draftCountRule === "interval" && (
          <div className="min-w-[100px]">
            <label className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
              Tage
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={draftCountIntervalDays}
              onChange={(e) => setDraftCountIntervalDays(e.target.value)}
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none"
            />
            {!daysValidation.ok && (
              <p className="mt-1 text-[10px] text-danger">{daysValidation.message}</p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!draftName.trim() || !eventType || !daysValidation.ok || create.isPending}
          className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
        >
          + Anlegen
        </button>
      </div>
      {shouldShowPurchaseHint(eventType, draftCountRule) && (
        <p className="rounded-md bg-warning-bg px-3 py-1.5 text-xs text-warning">
          Wiederholungskäufe zählen dann nicht als Umsatz.
        </p>
      )}

      {query.isLoading ? (
        <LoadingShell />
      ) : items.length === 0 ? (
        <p className="text-xs text-text-tertiary">
          Noch keine Endpoints — leg den ersten an um Make/Zapier loszuwerden.
        </p>
      ) : (
        <ul className="space-y-1">
          {items.map((e) => (
            <li
              key={e.id}
              className="flex items-center gap-3 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs"
            >
              <Link
                to={`/projects/${projectId}/webhook-endpoints/${e.id}`}
                className="flex-1 text-text-link hover:text-text-link-hover"
              >
                {e.name}
                <span className="ml-2 text-text-tertiary">
                  → /wh/{e.slug}
                </span>
              </Link>
              <span className="font-mono text-[10px] text-text-tertiary">
                {e.event_type_default}
              </span>
              <HqBadge mapping={e.mapping_json} />
              <EndpointSigningToggle
                projectId={projectId}
                endpointId={e.id}
                value={e.signing_required}
                signingDefault={signingDefault}
              />
              <span
                className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                  e.status === "active"
                    ? "bg-success/20 text-success"
                    : e.status === "disabled"
                      ? "bg-bg-tertiary text-text-tertiary"
                      : "bg-warning-bg text-warning"
                }`}
              >
                {e.status}
              </span>
              {showOrigin && e.activated_by && (
                <span className="text-[10px] text-text-tertiary">
                  {e.activated_by === "partner" ? "vom Kunden aktiviert" : "vom Team aktiviert"}
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!confirm(`Endpoint "${e.name}" wirklich löschen?`)) return;
                  remove.mutate(e.id, {
                    onSuccess: () => toast.success("Gelöscht"),
                  });
                }}
                className="text-text-tertiary hover:text-danger"
                title="Endpoint löschen"
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---- DHC-Kunde (Verknüpfung des Projekts mit einem DHC-Kunden) ----

function ProjectCustomerSection({ projectId }: { projectId: number }) {
  const customersQuery = useDhCustomers();
  const linkQuery = useProjectCustomerLink(projectId);
  const setLink = useSetProjectCustomerLink(projectId);

  const configured = customersQuery.data?.configured ?? false;
  const customers = customersQuery.data?.customers ?? [];
  const current = linkQuery.data?.kundeRecId ?? "";

  if (!configured) {
    return (
      <p className="text-xs text-text-tertiary">
        {customersQuery.isLoading
          ? "Lade…"
          : "Kundenverknüpfung nicht konfiguriert (DH_PORTAL_DATABASE_URL fehlt)."}
      </p>
    );
  }

  return (
    <>
      <Combobox
        value={current}
        disabled={linkQuery.isLoading || setLink.isPending}
        options={customers.map((k) => ({
          value: k.kundeRecId,
          label: k.company ?? k.kundeRecId,
        }))}
        onChange={(v) => {
          const val = v || null;
          setLink.mutate(val, {
            onSuccess: () =>
              toast.success(val ? "Kunde verknüpft" : "Verknüpfung gelöst"),
            onError: (err) =>
              toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
          });
        }}
        placeholder="DHC-Kunde suchen… (leeren = Verknüpfung lösen)"
      />
    </>
  );
}

// ---- Settings ----

export function ProjectSettingsTab() {
  const { project, projectId, isLoading } = useTabContext();
  const update = useUpdateProject(projectId);
  const rotate = useRotateSecret(projectId);
  const [name, setName] = useState("");
  const [showSecretConfirm, setShowSecretConfirm] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [rotateConfirmInput, setRotateConfirmInput] = useState("");

  useEffect(() => {
    if (project) setName(project.name);
  }, [project]);

  if (isLoading || !project) return <LoadingShell />;

  const dirty = name.trim() !== project.name && name.trim().length > 0;

  const saveName = () => {
    update.mutate(
      { name: name.trim() },
      {
        onSuccess: () => toast.success("Name gespeichert"),
        onError: (err) =>
          toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  const handleRotate = () => {
    if (!matchesRotateConfirmation(rotateConfirmInput, project.name)) {
      toast.error("Projektname stimmt nicht — Rotation abgebrochen");
      return;
    }
    rotate.mutate(undefined, {
      onSuccess: (res) => {
        setRevealedSecret(res.secret);
        toast.success("API-Secret rotiert");
      },
      onError: (err) =>
        toast.error(`Rotation fehlgeschlagen: ${(err as Error).message}`),
    });
    setShowSecretConfirm(false);
    setRotateConfirmInput("");
  };

  return (
    <>
      <SettingsPanel>
        <SettingRow title="Name" description="Der Name, unter dem dieses Projekt bei dir in der Übersicht erscheint.">
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-9 flex-1 rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
            />
            <button
              type="button"
              onClick={saveName}
              disabled={!dirty || update.isPending}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-60"
            >
              Speichern
            </button>
          </div>
        </SettingRow>

        {offersKundeLink(project.workspace) && (
          <SettingRow
            title="DHC-Kunde"
            description="Ordnet dieses Projekt einem Kunden in unserem System zu. Die oberste Option in der Liste hebt die Zuordnung wieder auf."
          >
            <ProjectCustomerSection projectId={projectId} />
          </SettingRow>
        )}

        <SettingRow
          title="Funnel-ID"
          description="Technische Kennung dieses Funnels — für die Anbindung an bestehende Auswertungen. Kann nicht geändert werden."
        >
          <p className="font-mono text-sm text-text-primary">{project.funnelId}</p>
        </SettingRow>

        <SettingRow
          title="Funnel Mark"
          description="Kurze technische Kennung des Funnels. Kann nicht geändert werden."
        >
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border border-border-primary bg-bg-tertiary px-3 py-1.5 font-mono text-xs text-text-primary">
              {project.funnelMark}
            </code>
            <button
              type="button"
              onClick={() => copyToClipboard(project.funnelMark, "Funnel Mark")}
              className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
            >
              <Copy size={12} /> Kopieren
            </button>
          </div>
        </SettingRow>

        <SettingRow
          title="BigQuery Dataset"
          description={
            <>
              Speicherort der Daten dieses Kunden in unserer Datenbank{" "}
              <code>dhc-ablecdp-kunden</code>. Wird beim Anlegen automatisch
              vergeben und kann nicht geändert werden.
            </>
          }
        >
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border border-border-primary bg-bg-tertiary px-3 py-1.5 font-mono text-xs text-text-primary">
              {project.bqDataset}
            </code>
            <button
              type="button"
              onClick={() => copyToClipboard(project.bqDataset, "Dataset-ID")}
              className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
            >
              <Copy size={12} /> Kopieren
            </button>
          </div>
        </SettingRow>

        <SettingRow
          title="Projekt-Geheimnis (Tracking-Kennung)"
          description="Die Kennung im Tracking-Skript des Kunden — nicht der Zugang für Kunden-Abfragen (den findest du unter Integrationen → Schnittstellen-Zugänge)."
          wide
        >
          <div className="rounded-md border border-danger bg-danger-bg/40 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-danger" />
              <p className="text-sm text-text-primary">
                <strong>Intern. Niemals an Kunden weitergeben.</strong> Das ist
                die Kennung im Tracking-Skript des Kunden — eine Rotation
                bricht sofort das Tracking, bis das Skript auf allen
                Kundenseiten ausgetauscht ist. Für Kunden-Abfragen: der
                Schnittstellen-Zugang im Tab Integrationen.
              </p>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 rounded-md border border-border-primary bg-bg-tertiary px-3 py-1.5 font-mono text-xs text-text-primary">
                {revealedSecret ?? project.apiSecretMasked}
              </code>
              {revealedSecret && (
                <button
                  type="button"
                  onClick={() => copyToClipboard(revealedSecret, "Secret")}
                  className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
                >
                  <Copy size={12} /> Kopieren
                </button>
              )}
              <button
                type="button"
                onClick={() => setRevealedSecret(null)}
                disabled={!revealedSecret}
                className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-50"
              >
                {revealedSecret ? <EyeOff size={12} /> : <Eye size={12} />}
                Verbergen
              </button>
              <button
                type="button"
                onClick={() => setShowSecretConfirm(true)}
                className="inline-flex items-center gap-1 rounded-md border border-danger bg-bg-primary px-2 py-1 text-xs font-medium text-danger hover:bg-danger-bg"
              >
                <RefreshCw size={12} /> Rotieren
              </button>
            </div>
            {revealedSecret && (
              <p className="mt-2 flex items-center gap-1 text-xs text-warning">
                <Check size={12} /> Neues Secret — jetzt kopieren, danach nicht
                mehr im Klartext sichtbar.
              </p>
            )}
          </div>
        </SettingRow>

        <SettingRow
          title="Storage-Engine"
          description="Technische Infos dazu, wie die Daten gespeichert und ob sie an BigQuery (Datenbank für Dashboards) übertragen werden."
          wide
        >
          <EngineSettings project={project} />
        </SettingRow>

        <SettingRow
          title="Gefahrenzone"
          description="Das Tracking dieses Projekts vorübergehend anhalten oder wieder einschalten."
          wide
        >
          <DangerZoneSection project={project} projectId={projectId} />
        </SettingRow>
      </SettingsPanel>

      <ConfirmDialog
        open={showSecretConfirm}
        onOpenChange={(open) => {
          setShowSecretConfirm(open);
          if (!open) setRotateConfirmInput("");
        }}
        title="Projekt-Geheimnis rotieren?"
        variant="danger"
        confirmLabel="Rotieren"
        description={
          <div className="space-y-2">
            <p>
              Das alte Geheimnis wird sofort ungültig. Das Tracking bricht auf
              jeder Seite mit dem eingebetteten Skript ab, bis das neue
              Geheimnis dort ausgetauscht ist.
            </p>
            <p>
              Betroffene Domains:{" "}
              {project.allowedDomains.length > 0
                ? project.allowedDomains.join(", ")
                : "keine hinterlegt — Tracking läuft auf allen Domains"}
            </p>
            <p>
              Zur Bestätigung tippe den Projektnamen exakt ab:
            </p>
            <code className="block rounded-md bg-bg-tertiary px-2 py-1 font-mono text-xs">
              {project.name}
            </code>
            <input
              type="text"
              value={rotateConfirmInput}
              onChange={(e) => setRotateConfirmInput(e.target.value)}
              placeholder="Projektname"
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1.5 font-mono text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
              autoFocus
            />
          </div>
        }
        onConfirm={handleRotate}
        confirmDisabled={!matchesRotateConfirmation(rotateConfirmInput, project.name)}
      />
    </>
  );
}

// ---- Datenqualität ----

export function ProjectQualityTab() {
  const { project, isLoading, projectId } = useTabContext();

  if (isLoading || !project) return <LoadingShell />;

  return (
    <div className="space-y-6">
      <TrackingQualitySection projectId={projectId} />

      <SettingsPanel>
        <SettingRow
          title="Advanced Analytics"
          description="Misst, wie lange Besucher auf einer Seite bleiben und wie weit sie scrollen. Standardmäßig aus — erzeugt viele Daten und lohnt sich nur, wenn du diese Auswertungen wirklich nutzt."
        >
          <EngagementTrackingSection project={project} />
        </SettingRow>

        <SettingRow
          title="Merge-Denylist"
          description="Werte, die nicht mehr zur Zusammenführung von Personen genutzt werden sollen — z. B. Test-E-Mails oder Fake-Telefonnummern, die viele verschiedene Leute benutzen. Die Ereignisse werden weiter erfasst, nur das Zusammenführen wird verhindert."
          wide
        >
          <DenylistSection projectId={projectId} />
        </SettingRow>

        <SettingRow
          title="Merge-Review"
          description="Hält verdächtige Zusammenführungen von Personen an, damit du sie von Hand prüfen kannst, bevor sie gespeichert werden."
        >
          <MergeReviewSection project={project} projectId={projectId} />
        </SettingRow>
      </SettingsPanel>
    </div>
  );
}

// ---- Merge-Review (toggle) ----

function MergeReviewSection({
  project,
  projectId,
}: {
  project: ProjectDetail;
  projectId: number;
}) {
  const toggleMergeReview = useToggleMergeReview(projectId);
  return (
    <>
      <p className="mb-3 text-sm text-text-secondary">
        Verdächtige Zusammenführungen von Personen werden angehalten, damit ein
        Mensch sie prüfen kann, bevor sie gespeichert werden. Das hilft z. B. bei
        Fake-Telefonnummern oder wenn ein Mitarbeiter mehrere Kunden-Links
        angeklickt hat und dadurch fälschlich mit vielen anderen verschmilzt.
      </p>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() =>
            toggleMergeReview.mutate(!project.mergeReviewEnabled, {
              onSuccess: (res) =>
                toast.success(
                  res.mergeReviewEnabled
                    ? "Merge-Review aktiviert"
                    : "Merge-Review pausiert",
                ),
              onError: (err) =>
                toast.error(
                  `Schalten fehlgeschlagen: ${(err as Error).message}`,
                ),
            })
          }
          disabled={toggleMergeReview.isPending}
          className={`rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-60 ${
            project.mergeReviewEnabled
              ? "border border-border-primary bg-bg-secondary text-text-secondary hover:bg-bg-hover"
              : "bg-accent text-text-on-accent hover:bg-accent-hover"
          }`}
        >
          {project.mergeReviewEnabled
            ? "Merge-Review pausieren"
            : "Merge-Review aktivieren"}
        </button>
        <span
          className={`text-xs font-medium ${
            project.mergeReviewEnabled ? "text-success" : "text-warning"
          }`}
        >
          {project.mergeReviewEnabled ? "Aktiv" : "Pausiert"}
        </span>
      </div>
    </>
  );
}

// ---- Danger ----

function DangerZoneSection({
  project,
  projectId,
}: {
  project: ProjectDetail;
  projectId: number;
}) {
  const toggle = useToggleActive(projectId);
  const archive = useArchiveProject(projectId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const archived = project.archivedAt != null;

  const applyArchive = () => {
    archive.mutate(!archived, {
      onSuccess: (res) => {
        if (archived) toast.success("Projekt wiederhergestellt, es ist pausiert");
        else if (res.hostnameRemoved === false)
          toast.warning("Projekt archiviert. Die eigene Tracking-Adresse konnte nicht abgemeldet werden, bitte erneut archivieren.");
        else toast.success("Projekt archiviert");
      },
      onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
    });
    setArchiveOpen(false);
  };

  if (archived) {
    return (
      <div className="space-y-6">
        <div className="rounded-md border border-border-primary bg-bg-secondary p-5">
          <p className="font-medium text-text-primary">
            Archiviert seit {new Date(project.archivedAt!).toLocaleDateString("de-DE")}
          </p>
          <p className="mt-1 text-sm text-text-secondary">
            Skript, Ereignisse und Webhooks werden abgelehnt, an Meta und Google geht nichts mehr raus.
            Die Daten sind erhalten. Wiederherstellen holt das Projekt pausiert zurück. Ziele,
            Kunden-Zugänge und die eigene Tracking-Adresse bleiben danach aus und müssen neu eingerichtet werden.
          </p>
          <button
            type="button"
            onClick={() => setArchiveOpen(true)}
            className="mt-4 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover"
          >
            Wiederherstellen
          </button>
        </div>
        <ConfirmDialog
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          title="Projekt wiederherstellen?"
          confirmLabel="Wiederherstellen"
          description={`„${project.name}" kommt pausiert zurück. Eingeschaltet wird es erst, wenn du es aktivierst.`}
          onConfirm={applyArchive}
        />
      </div>
    );
  }

  const apply = () => {
    toggle.mutate(!project.active, {
      onSuccess: () =>
        toast.success(project.active ? "Projekt pausiert" : "Projekt aktiviert"),
      onError: (err) =>
        toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
    });
    setConfirmOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-md border border-danger bg-danger-bg/40 p-5">
        <p className="font-medium text-text-primary">
          {project.active ? "Tracking pausieren" : "Tracking reaktivieren"}
        </p>
        <p className="mt-1 text-sm text-text-secondary">
          {project.active
            ? "Danach werden keine neuen Ereignisse mehr angenommen (wirkt nach etwa einer Minute). Bereits erfasste Daten bleiben erhalten."
            : "Schaltet das Tracking wieder ein. Wirkt nach etwa einer Minute."}
        </p>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className={`mt-4 rounded-md px-3 py-1.5 text-sm font-medium ${
            project.active
              ? "bg-danger text-text-inverse hover:opacity-90"
              : "bg-accent text-text-on-accent hover:bg-accent-hover"
          }`}
        >
          {project.active ? "Pausieren" : "Aktivieren"}
        </button>
      </div>

      <div className="rounded-md border border-danger bg-danger-bg/40 p-5">
        <p className="font-medium text-text-primary">Projekt archivieren</p>
        <p className="mt-1 text-sm text-text-secondary">
          Für Kunden, die nicht mehr betreut werden. Sperrt alles dauerhaft und blendet das Projekt
          aus der Liste aus. Die Daten bleiben erhalten.
        </p>
        <button
          type="button"
          onClick={() => setArchiveOpen(true)}
          className="mt-4 rounded-md bg-danger px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
        >
          Archivieren
        </button>
      </div>

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="Projekt archivieren?"
        variant="danger"
        confirmLabel="Archivieren"
        description={`„${project.name}" lehnt danach Skript, Ereignisse und Webhooks ab. Alle Ziele werden ausgeschaltet, wartende Sendungen an Meta und Google verfallen, Kunden-Zugänge werden entzogen und die eigene Tracking-Adresse wird abgemeldet. Die Daten bleiben erhalten.`}
        onConfirm={applyArchive}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={project.active ? "Tracking pausieren?" : "Tracking aktivieren?"}
        variant={project.active ? "danger" : "default"}
        confirmLabel={project.active ? "Pausieren" : "Aktivieren"}
        description={
          project.active
            ? `„${project.name}" stoppt eingehende Events. Wirkt nach max. 60s.`
            : `„${project.name}" nimmt wieder Events entgegen.`
        }
        onConfirm={apply}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Webhook-Signing — reveal/copy/rotate secret + signing_required toggle.
//
// The secret is server-generated (pgcrypto). We never display it on initial
// load — the user has to click Anzeigen, mirroring the api_secret pattern.
// Rotation is destructive (old secret stops working immediately), so it
// goes through a ConfirmDialog like rotate-secret does.
// ---------------------------------------------------------------------------

function WebhookSigningSection({ project }: { project: ProjectDetail }) {
  const reveal = useRevealWebhookSecret(project.id);
  const rotate = useRotateWebhookSecret(project.id);
  const toggleSigning = useToggleWebhookSigning(project.id);
  const [shown, setShown] = useState<string | null>(null);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const doReveal = () => {
    reveal.mutate(undefined, {
      onSuccess: (res) => setShown(res.secret),
      onError: (err) =>
        toast.error(`Secret laden fehlgeschlagen: ${(err as Error).message}`),
    });
  };

  const copy = async () => {
    if (!shown) return;
    await navigator.clipboard.writeText(shown);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const doRotate = () => {
    rotate.mutate(undefined, {
      onSuccess: (res) => {
        setShown(res.secret);
        toast.success(
          "Webhook-Secret rotiert — alte Signatur wird ab sofort abgelehnt",
        );
      },
      onError: (err) =>
        toast.error(`Rotation fehlgeschlagen: ${(err as Error).message}`),
    });
    setShowRotateConfirm(false);
  };

  const flipRequired = () => {
    toggleSigning.mutate(!project.webhookSigningRequired, {
      onSuccess: (res) =>
        toast.success(
          res.webhookSigningRequired
            ? "Signatur-Pflicht aktiviert — Sender ohne gültigen X-DHC-Key bekommen 401"
            : "Signatur-Pflicht deaktiviert — Schlüssel wird verifiziert wenn vorhanden, aber nicht erzwungen",
        ),
      onError: (err) =>
        toast.error(`Schalten fehlgeschlagen: ${(err as Error).message}`),
    });
  };

  const required = project.webhookSigningRequired;
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border-primary bg-bg-secondary p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">Signatur erzwingen</span>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  required ? "bg-success/20 text-success" : "bg-bg-tertiary text-text-tertiary"
                }`}
              >
                {required ? "Pflicht" : "Optional"}
              </span>
            </div>
            <p className="mt-1 text-xs text-text-secondary">
              {required
                ? "Aktiv. Achtung: Das gilt auch für die automatisch angebundenen Tools — wenn die keinen Schlüssel mitschicken, funktionieren sie nicht mehr. Du kannst das unten je Tool einzeln ausnehmen."
                : "Aus. Ein Schlüssel wird geprüft, wenn er mitgeschickt wird, aber nicht verlangt — sicher zum Ausprobieren."}
            </p>
          </div>
          <button
            type="button"
            onClick={flipRequired}
            disabled={toggleSigning.isPending}
            className={`rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-60 ${
              required
                ? "border border-border-primary bg-bg-primary text-text-secondary hover:bg-bg-hover"
                : "bg-accent text-text-on-accent hover:bg-accent-hover"
            }`}
          >
            {required ? "Deaktivieren" : "Aktivieren"}
          </button>
        </div>
      </div>

      {required && (
        <>
          <div className="rounded-md border border-border-primary bg-bg-secondary p-3 text-sm text-text-secondary">
            <p className="mb-2 font-medium text-text-primary">So fügst du den Schlüssel hinzu (Make/Zapier):</p>
            <ol className="list-decimal space-y-1 pl-4 text-xs">
              <li>Secret unten anzeigen + kopieren.</li>
              <li>
                Im Webhook-Modul einen Header ergänzen:{" "}
                <code className="rounded bg-bg-tertiary px-1 font-mono">X-DHC-Key: &lt;Secret&gt;</code>
              </li>
              <li>Fertig — Sendungen ohne diesen Header (oder mit falschem Wert) werden abgewiesen.</li>
            </ol>
            <p className="mt-2 text-[11px] text-text-tertiary">
              Fortgeschritten: alternativ HMAC über{" "}
              <code className="rounded bg-bg-tertiary px-1 font-mono">X-DHC-Signature: sha256=&lt;hex&gt;</code> (body-gebunden).
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
            <code className="flex-1 truncate font-mono text-xs text-text-primary" aria-label="Webhook-Secret">
              {shown ?? "•".repeat(40)}
            </code>
            {!shown && (
              <button
                type="button"
                onClick={doReveal}
                disabled={reveal.isPending}
                className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-50"
              >
                <Eye size={12} /> Anzeigen
              </button>
            )}
            {shown && (
              <>
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? "Kopiert" : "Kopieren"}
                </button>
                <button
                  type="button"
                  onClick={() => setShown(null)}
                  className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover"
                >
                  <EyeOff size={12} /> Verbergen
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setShowRotateConfirm(true)}
              className="inline-flex items-center gap-1 rounded-md border border-danger bg-bg-primary px-2 py-1 text-xs font-medium text-danger hover:bg-danger-bg"
            >
              <RefreshCw size={12} /> Rotieren
            </button>
          </div>
        </>
      )}

      <ConfirmDialog
        open={showRotateConfirm}
        onOpenChange={setShowRotateConfirm}
        title="Webhook-Secret rotieren?"
        variant="danger"
        confirmLabel="Rotieren"
        description={
          <>
            Das alte Secret wird sofort ungültig. Alle Partner, die signierte
            Requests schicken, müssen das neue Secret bekommen — sonst gibt es
            ab sofort 401 für deren Calls.
          </>
        }
        onConfirm={doRotate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Engine settings — bq_sync toggle only.
//
// identity_engine ist im UI bewusst NICHT exponiert: jedes Projekt läuft
// Postgres-primary, das ist die einzig sinnvolle Wahl in Production.
// 'dual' und 'bigquery' (Legacy) bleiben im DB-Schema für Notfälle und
// Cutover-Validation, aber Operator setzen die via psql, nicht über die UI —
// sonst landet man versehentlich auf dem Legacy-Pfad.
// ---------------------------------------------------------------------------

function EngineSettings({ project }: { project: ProjectDetail }) {
  const updateEngine = useUpdateEngine(project.id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");

  const enableBqSync = () => {
    updateEngine.mutate(
      { bqSyncEnabled: true },
      {
        onSuccess: () => toast.success("BigQuery-Sync aktiviert"),
        onError: (err) =>
          toast.error(`Aktivieren fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  const disableBqSync = () => {
    if (confirmInput.trim() !== DISABLE_BQ_SYNC_CONFIRM_PHRASE) {
      toast.error("Bestätigungs-Text stimmt nicht");
      return;
    }
    updateEngine.mutate(
      {
        bqSyncEnabled: false,
        confirmDisableBqSync: DISABLE_BQ_SYNC_CONFIRM_PHRASE,
      },
      {
        onSuccess: () => {
          toast.success("BigQuery-Sync deaktiviert");
          setConfirmOpen(false);
          setConfirmInput("");
        },
        onError: (err) =>
          toast.error(`Deaktivieren fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  const bqSyncCanBeDisabled = project.identityEngine !== "bigquery";
  const onLegacyEngine = project.identityEngine !== "postgres";

  return (
    <div className="space-y-5">
      {/* Engine-Status read-only: Postgres ist die Norm, alles andere ein
          Migrations- oder Notfall-Zustand der via SQL gesetzt wird. */}
      <div className="rounded-md border border-border-primary bg-bg-secondary p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary">
            Identity-Engine
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
              onLegacyEngine
                ? "bg-warning-bg text-warning"
                : "bg-success/20 text-success"
            }`}
          >
            {project.identityEngine}
          </span>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          {project.identityEngine === "postgres"
            ? "Postgres-primary: Identity-Resolution mit ms-Latenz, BigQuery wird asynchron gespiegelt."
            : project.identityEngine === "dual"
              ? "Cutover-Modus: BigQuery ist Source of Truth, Postgres läuft als Shadow mit Diff-Logs. Sollte nach Migrations-Validierung wieder auf 'postgres' wechseln."
              : "Legacy-Pfad: Identity-Resolution gegen BigQuery (höhere Latenz, Streaming-Buffer-Lag). Sollte zu 'postgres' migriert werden."}
        </p>
      </div>

      <div className="rounded-md border border-border-primary bg-bg-secondary p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">
                BigQuery-Sync
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  project.bqSyncEnabled
                    ? "bg-success/20 text-success"
                    : "bg-warning-bg text-warning"
                }`}
              >
                {project.bqSyncEnabled ? "Aktiv" : "Aus"}
              </span>
            </div>
            <p className="mt-1 text-xs text-text-secondary">
              Schickt die Daten zusätzlich an BigQuery — die Grundlage für
              Looker-Dashboards und externe Auswertungen. Standardmäßig an. Nur
              abschalten, wenn der Kunde keine solchen Reports nutzt.
            </p>
            {project.bqSyncLastAt && (
              <p className="mt-1 font-mono text-[10px] text-text-tertiary">
                Letzter Sync: {new Date(project.bqSyncLastAt).toLocaleString("de-DE")}
              </p>
            )}
          </div>
          {project.bqSyncEnabled ? (
            <button
              type="button"
              disabled={!bqSyncCanBeDisabled || updateEngine.isPending}
              onClick={() => setConfirmOpen(true)}
              title={
                bqSyncCanBeDisabled
                  ? "BigQuery-Sync deaktivieren"
                  : "Mit Engine='bigquery' kann BQ-Sync nicht aus sein"
              }
              className="rounded-md border border-danger bg-bg-primary px-3 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger-bg disabled:opacity-50"
            >
              Deaktivieren …
            </button>
          ) : (
            <button
              type="button"
              onClick={enableBqSync}
              disabled={updateEngine.isPending}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent transition-colors hover:bg-accent-hover disabled:opacity-50"
            >
              Aktivieren
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setConfirmInput("");
        }}
        title="BigQuery-Sync deaktivieren?"
        confirmDisabled={confirmInput.trim() !== DISABLE_BQ_SYNC_CONFIRM_PHRASE}
        variant="danger"
        confirmLabel="Deaktivieren"
        description={
          <div className="space-y-2">
            <p>
              Dieses Projekt schreibt danach keine Events mehr nach BigQuery.
              Looker-Studio / externe BI-Pipelines des Kunden bekommen keine
              neuen Daten — bestehende historische Daten in BQ bleiben.
            </p>
            <p>
              Zur Bestätigung tippe folgenden Text exakt ab:
            </p>
            <code className="block rounded-md bg-bg-tertiary px-2 py-1 font-mono text-xs">
              {DISABLE_BQ_SYNC_CONFIRM_PHRASE}
            </code>
            <input
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder="Bestätigungs-Text"
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1.5 font-mono text-xs text-text-primary placeholder:text-text-tertiary focus:border-accent focus:outline-none"
              autoFocus
            />
          </div>
        }
        onConfirm={disableBqSync}
      />
    </div>
  );
}
